/**
 * Platform admin Users tab: every account with plan, status and this-period
 * usage, filterable by account type, with usage reset and deletion.
 */
import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { ADMIN_COLORS } from '@/components/admin/AdminUI';
import { useTranslation } from '../../../../hooks/useTranslation';
import { ROUTES } from '../../../../navigation/routes';
import {
  filterUsersByAccountType,
  formatAnalysisUsage,
  formatRenewalDate,
  formatSubscriptionSource,
  formatUsageMinutes,
  formatUsagePeriodStart,
  formatUsageScope,
  formatUserPackage,
  formatUserStatus,
  getStatusColor,
} from '../platformAdminFormat';
import type { PlatformUser } from '../platformAdminTypes';
import type { UserActions } from '../hooks/useUserActions';

interface UsersTabProps {
  platformUsers: PlatformUser[];
  userActions: UserActions;
  onManageSchools: () => void;
}

export default function UsersTab({ platformUsers, userActions, onManageSchools }: UsersTabProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    userFilter,
    setUserFilter,
    resettingUserId,
    deletingUserId,
    handleResetUsage,
    handleDeleteUser,
  } = userActions;
  const filteredUsers = filterUsersByAccountType(platformUsers, userFilter);

  return (
    <View className="gap-4">
      <View className="flex-row justify-between items-center">
        <Text className="text-xl font-bold text-foreground">
          {t('platformAdmin.usersRoster')} ({filteredUsers.length})
        </Text>
        <Text className="text-sm text-muted-foreground">
          {t('platformAdmin.usersSummary')}
        </Text>
      </View>

      <View className="flex-row gap-2">
        {(['all', 'b2c', 'b2b2c'] as const).map((filterKey) => (
          <Button
            key={filterKey}
            variant={userFilter === filterKey ? 'default' : 'outline'}
            size="sm"
            onPress={() => setUserFilter(filterKey)}
          >
            <Text className={`${userFilter === filterKey ? 'text-white' : 'text-foreground'} text-xs font-semibold`}>
              {filterKey === 'all'
                ? t('platformAdmin.userFilterAll')
                : filterKey === 'b2c'
                  ? t('platformAdmin.userFilterB2C')
                  : t('platformAdmin.userFilterB2B2C')}
            </Text>
          </Button>
        ))}
      </View>

      {filteredUsers.length > 0 ? (
        <View className="gap-3">
          {filteredUsers.map((userItem) => (
            <Card key={userItem.id}>
              <CardContent className="p-4">
                <View className="flex-row justify-between items-start mb-3">
                  <View className="flex-1 mr-3">
                    <View className="flex-row items-center gap-2">
                      <Text className="text-base font-semibold text-foreground">
                        {userItem.full_name || t('platformAdmin.userNameFallback')}
                      </Text>
                      <View className={`px-2 py-0.5 rounded-full ${userItem.account_type === 'b2c' ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                        <Text className={`text-[10px] font-semibold ${userItem.account_type === 'b2c' ? 'text-blue-700' : 'text-emerald-700'}`}>
                          {userItem.account_type === 'b2c'
                            ? t('platformAdmin.accountTypeB2C')
                            : t('platformAdmin.accountTypeB2B2C')}
                        </Text>
                      </View>
                    </View>
                    <Text className="text-sm text-muted-foreground">
                      {userItem.email}
                    </Text>
                    <Text className="text-xs text-muted-foreground mt-1">
                      {t('platformAdmin.userJoined')}: {new Date(userItem.created_at).toLocaleDateString()}
                    </Text>
                  </View>
                  <View
                    className={`px-3 py-1 rounded-full ${userItem.is_active ? 'bg-green-100' : 'bg-red-100'
                      }`}
                  >
                    <Text
                      className={`text-xs font-semibold ${userItem.is_active ? 'text-green-700' : 'text-red-700'
                        }`}
                    >
                      {userItem.is_active ? t('schoolManagement.active') : t('schoolManagement.inactive')}
                    </Text>
                  </View>
                </View>

                <View className="gap-2">
                  <View className="flex-row items-center">
                    <MaterialIcons name="school" size={16} color="#6b7280" />
                    <Text className="text-sm text-muted-foreground ml-2 flex-1">
                      {t('platformAdmin.userSchool')}: {userItem.school_name || t('platformAdmin.userSchoolUnknown')}
                    </Text>
                  </View>
                  <View className="flex-row items-center">
                    <MaterialIcons name="workspace-premium" size={16} color="#6b7280" />
                    <Text className="text-sm text-muted-foreground ml-2 flex-1">
                      {t('platformAdmin.userPackage')}: {formatUserPackage(t, userItem)}
                    </Text>
                  </View>
                  <View className="flex-row items-center">
                    <MaterialIcons
                      name="verified-user"
                      size={16}
                      color={getStatusColor(ADMIN_COLORS, userItem.subscription_status)}
                    />
                    <Text
                      className="text-sm ml-2 flex-1"
                      style={{ color: getStatusColor(ADMIN_COLORS, userItem.subscription_status) }}
                    >
                      {t('platformAdmin.userStatus')}: {formatUserStatus(t, userItem)}
                    </Text>
                  </View>
                  <View className="flex-row items-center">
                    <MaterialIcons name="update" size={16} color="#6b7280" />
                    <Text className="text-sm text-muted-foreground ml-2 flex-1">
                      {t('platformAdmin.userRenewal')}: {formatRenewalDate(t, userItem.subscription_renews_at)}
                    </Text>
                  </View>
                  {formatSubscriptionSource(t, userItem) ? (
                    <View className="flex-row items-center">
                      <MaterialIcons name="info" size={16} color="#6b7280" />
                      <Text className="text-xs text-muted-foreground ml-2 flex-1">
                        {formatSubscriptionSource(t, userItem)}
                      </Text>
                    </View>
                  ) : null}

                  {/* Usage Statistics */}
                  <View
                    className="flex-row flex-wrap items-center mt-2 pt-2 border-t border-gray-200"
                    style={{ columnGap: 16, rowGap: 8 }}
                  >
                    <View className="flex-row items-center">
                      <MaterialIcons name="calendar-today" size={16} color="#6b7280" />
                      <Text className="text-sm text-muted-foreground ml-2">
                        Usage period: {formatUsagePeriodStart(userItem)}
                      </Text>
                    </View>
                    <View className="flex-row items-center">
                      <MaterialIcons name="description" size={16} color="#6b7280" />
                      <Text className="text-sm text-muted-foreground ml-2">
                        {t('platformAdmin.transcriptsUsage')}: {formatAnalysisUsage(userItem)}
                      </Text>
                    </View>
                    <View className="flex-row items-center">
                      <MaterialIcons name="mic" size={16} color="#6b7280" />
                      <Text className="text-sm text-muted-foreground ml-2">
                        {t('platformAdmin.transcriptionMinutesUsage')}: {formatUsageMinutes(userItem)} min
                      </Text>
                    </View>
                    <View className="flex-row items-center">
                      <MaterialIcons name="chat" size={16} color="#6b7280" />
                      <Text className="text-sm text-muted-foreground ml-2">
                        {t('platformAdmin.chatMessagesUsage')}: {userItem.chat_messages_this_month || 0}
                      </Text>
                    </View>
                    <View className="flex-row items-center">
                      <MaterialIcons name="account-balance-wallet" size={16} color="#6b7280" />
                      <Text className="text-sm text-muted-foreground ml-2">
                        Scope: {formatUsageScope(userItem)}
                      </Text>
                    </View>
                  </View>
                </View>

                <View className="flex-row justify-end mt-4 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={resettingUserId === userItem.id}
                    onPress={() => handleResetUsage(userItem)}
                  >
                    {resettingUserId === userItem.id ? (
                      <>
                        <ActivityIndicator size="small" color="#1F2937" />
                        <Text className="text-xs text-foreground ml-2">
                          {t('platformAdmin.resettingUsage')}
                        </Text>
                      </>
                    ) : (
                      <>
                        <MaterialIcons name="refresh" size={16} color="#1F2937" />
                        <Text className="text-xs text-foreground ml-1">
                          {t('platformAdmin.resetUsage')}
                        </Text>
                      </>
                    )}
                  </Button>
                  {userItem.school_id ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onPress={() =>
                        router.push({
                          pathname: ROUTES.TABS_SCHOOL_MANAGEMENT,
                          params: { schoolId: String(userItem.school_id), tab: 'students' },
                        })
                      }
                    >
                      <MaterialIcons name="open-in-new" size={16} color="#1F2937" />
                      <Text className="text-xs text-foreground ml-1">
                        {t('platformAdmin.viewSchool')}
                      </Text>
                    </Button>
                  ) : null}
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deletingUserId === userItem.id}
                    onPress={() => handleDeleteUser(userItem)}
                  >
                    {deletingUserId === userItem.id ? (
                      <>
                        <ActivityIndicator size="small" color="#ffffff" />
                        <Text className="text-xs text-white ml-1">
                          {t('platformAdmin.deletingUser')}
                        </Text>
                      </>
                    ) : (
                      <>
                        <MaterialIcons name="delete" size={16} color="#ffffff" />
                        <Text className="text-xs text-white ml-1">
                          {t('platformAdmin.deleteUser')}
                        </Text>
                      </>
                    )}
                  </Button>
                </View>
              </CardContent>
            </Card>
          ))}
        </View>
      ) : (
        <Card>
          <CardContent className="p-8 items-center">
            <MaterialIcons name="group" size={42} color="#6b7280" />
            <Text className="text-lg font-medium text-foreground mt-4 mb-2">
              {t('platformAdmin.noUsers')}
            </Text>
            <Text className="text-base text-muted-foreground text-center mb-4">
              {t('platformAdmin.addUsersHint')}
            </Text>
            <Button variant="outline" onPress={onManageSchools}>
              <Text className="text-sm text-foreground font-semibold">
                {t('platformAdmin.manageSchoolsCta')}
              </Text>
            </Button>
          </CardContent>
        </Card>
      )}
    </View>
  );
}
